import pluralize from "pluralize"
import PropTypes from "prop-types"
import React from "react"

import Button from "src/components/Button"
import Modal from "src/components/Modal"

import { closeWorkOrder } from "src/api/Service/WorkOrders"

import { CLOSE_WORK_ORDER_PREFIX } from "./amplitude_events"
import useWorkOrderActionMutation from "./hooks/use_work_order_action_mutation"

const ReadinessItem = ({ icon, children }) => (
  <div className="flex items-center gap-2 text-sm text-gray-700">
    <i className={`fa-kit ${icon}`} />
    <span>{children}</span>
  </div>
)
ReadinessItem.propTypes = {
  icon: PropTypes.string.isRequired,
  children: PropTypes.node,
}

const CloseWorkOrderModal = ({
  isOpen,
  onClose,
  workOrder,
  workOrderSequentialId,
}) => {
  const { mutate, isLoading, handleCancel } = useWorkOrderActionMutation({
    workOrderSequentialId,
    action: closeWorkOrder,
    eventPrefix: CLOSE_WORK_ORDER_PREFIX,
    successMessage: "Work order closed.",
    errorMessage: "Failed to close work order.",
    onClose,
  })

  const customerName = workOrder.contact?.name || workOrder.contact?.email
  const boatName = workOrder.contactBoat?.name

  const jobs = workOrder.jobs || []
  const totalJobs = jobs.length
  const invoicedJobs = jobs.filter((job) => job.status === "invoiced").length
  const unsettledCount = workOrder.unsettledInvoiceCount || 0

  const allJobsInvoiced = totalJobs > 0 && invoicedJobs === totalJobs
  const allInvoicesSettled = unsettledCount === 0

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="default">
      <Modal.Header>
        <h4 className="m-0 text-xl font-bold">Close Work Order?</h4>
        <div className="mt-1 text-sm text-gray-500">
          {workOrder.displaySequentialId}
          {customerName ? ` · ${customerName}` : ""}
          {boatName ? ` · ${boatName}` : ""}
        </div>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-4 text-sm text-gray-600">
          This will lock the work order from new work and remove it from the
          active queue. Existing invoices can still be settled and line items
          can still be removed (with refund where applicable). You can reopen
          the work order if you need to make further changes.
        </p>
        <div className="flex flex-col gap-2 rounded-lg border bg-gray-100 p-3">
          <ReadinessItem
            icon={
              allJobsInvoiced
                ? "fa-sf-check text-green-600"
                : "fa-warning-outline text-yellow-600"
            }
          >
            {invoicedJobs} of {totalJobs} {pluralize("job", totalJobs)} invoiced
          </ReadinessItem>
          <ReadinessItem
            icon={
              allInvoicesSettled
                ? "fa-sf-check text-green-600"
                : "fa-clock text-gray-500"
            }
          >
            {allInvoicesSettled
              ? "All invoices settled"
              : "Some invoices still outstanding (AR will continue to track)"}
          </ReadinessItem>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={() => mutate()}
            isLoading={isLoading}
          >
            Close Work Order
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

CloseWorkOrderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workOrder: PropTypes.shape({
    displaySequentialId: PropTypes.string,
    contact: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
    contactBoat: PropTypes.shape({ name: PropTypes.string }),
    jobs: PropTypes.arrayOf(PropTypes.shape({ status: PropTypes.string })),
    unsettledInvoiceCount: PropTypes.number,
  }).isRequired,
  workOrderSequentialId: PropTypes.string.isRequired,
}

export default CloseWorkOrderModal
